import { Button, Typography } from '@material-ui/core'
import React from 'react'

import { formatPromotionMinorAmount } from '../../../../helpers/promotion'
import { getTranslate } from '../../../../localization'
import { CartItem } from '../../../../types/cart'
import { EvaluatedPromotion } from '../../../../types/promotion'
import useStyles from './styles'

type AppliedOfferProps = {
  applied: EvaluatedPromotion
  message: string
  totalDiscountMinor?: string
  totalCurrency?: string
  cartItems?: CartItem[]
  disabled: boolean
  onRemove: () => void
  /** Fallback focus target, used when the Remove button is disabled and cannot take focus. */
  rootRef?: React.Ref<HTMLDivElement>
  /** Preferred focus target — a real control with its own name, not a summary of the outcome. */
  removeRef?: React.Ref<HTMLButtonElement>
}

/**
 * What the guest actually saved, in money.
 *
 * The section previously said "{name} applied" and stopped there — the guest was told a
 * promotion had worked and never what it was worth, on the one surface where the number is
 * the whole point of entering a code.
 *
 * `discountMinor` is computed against THIS cart by the promotion service. It is not
 * derived here from the offer's advertised rate and a subtotal — reconstructing a figure
 * the service owns is the bug class that made cart's pre-discount total wrong.
 *
 * That argument is about the VALUE, not its presentation, and an earlier version of this
 * comment was read as covering both. FORMATTING still has to happen here, and it must go
 * through `formatPromotionMinorAmount`: the wire value is in minor units, and rendering it
 * raw overstates the saving hundredfold in every currency with minor digits. The string
 * stays a string throughout — money over the wire is typed string so it never becomes an
 * IEEE-754 double.
 *
 * It replaces the code field rather than sitting under it, so Remove is the way back — an
 * editable input beside the word "applied" invites an edit that silently invalidates the
 * verdict next to it.
 */
const AppliedOffer = ({
  applied,
  message,
  totalDiscountMinor,
  totalCurrency,
  cartItems = [],
  disabled,
  onRemove,
  rootRef,
  removeRef
}: AppliedOfferProps) => {
  const classes = useStyles()
  const noCodeSaving = /^0+$/.test(applied.discountMinor)
  const cartItemsByID = new Map(cartItems.map((item) => [item.id, item]))
  const pairedLineIDs = applied.appliedLineIds
  const allocations = applied.allocations ?? []

  const roomLabel = (reference: string, index: number) => {
    const pairedID =
      pairedLineIDs?.length === allocations.length
        ? pairedLineIDs[index]
        : undefined
    const item =
      cartItemsByID.get(reference) ??
      (pairedID ? cartItemsByID.get(pairedID) : undefined)
    return item
      ? `${item.roomType.name} (${item.ratePlan.name})`
      : getTranslate('Selected room')
  }

  return (
    // NO role="status" here any more, and no announcement — see index.tsx, which owns both.
    //
    // This node was both announcing and receiving focus, and it could not reliably do
    // either. It mounts already containing its text, and a live region inserted
    // pre-populated is commonly not announced at all; where it IS announced, moving focus
    // onto the same node in the same tick either doubles the utterance or flushes the
    // pending polite queue mid-word. Focusing a live region is the one placement that
    // guarantees the two collide.
    //
    // tabIndex -1 stays: it is the FALLBACK focus target for when Remove is disabled and
    // therefore cannot take focus. Programmatically focusable, not in the tab order.
    <div className={classes.appliedRow} tabIndex={-1} ref={rootRef}>
      <div className={classes.appliedHeader}>
        <div className={classes.appliedText}>
          <Typography
            variant="body1"
            className={noCodeSaving ? classes.noSaving : classes.appliedAmount}>
            {message}
          </Typography>
        </div>
        <Button
          ref={removeRef}
          color="primary"
          disabled={disabled}
          onClick={onRemove}
          size="small"
          variant="text">
          {getTranslate('Remove')}
        </Button>
      </div>
      <div className={classes.savingsRow}>
        <Typography variant="body2">{getTranslate('Code discount')}</Typography>
        <Typography
          variant="body2"
          dir="ltr"
          className={noCodeSaving ? classes.noSaving : classes.appliedAmount}>
          {`${noCodeSaving ? '' : '− '}${formatPromotionMinorAmount(
            applied.discountMinor,
            applied.currency
          )} ${getTranslate(applied.currency)}`}
        </Typography>
      </div>
      {allocations.length > 0 && cartItems.length > 1 && (
        <div className={classes.allocationList}>
          {allocations.map((allocation, index) => (
            <div
              className={classes.allocationRow}
              key={`${allocation.roomStayReference}-${index}`}>
              <Typography variant="caption" color="textSecondary">
                {roomLabel(allocation.roomStayReference, index)}
              </Typography>
              <Typography variant="caption" color="textSecondary" dir="ltr">
                {`− ${formatPromotionMinorAmount(
                  allocation.discountMinor,
                  allocation.currency || applied.currency
                )} ${getTranslate(allocation.currency || applied.currency)}`}
              </Typography>
            </div>
          ))}
        </div>
      )}
      {totalDiscountMinor !== undefined &&
        totalCurrency &&
        totalDiscountMinor !== applied.discountMinor && (
          <div className={classes.totalSavings}>
            <Typography variant="body2">
              {getTranslate('Total promotion savings')}
            </Typography>
            <Typography variant="body2" dir="ltr">
              {`${
                /^0+$/.test(totalDiscountMinor) ? '' : '− '
              }${formatPromotionMinorAmount(
                totalDiscountMinor,
                totalCurrency
              )} ${getTranslate(totalCurrency)}`}
            </Typography>
          </div>
        )}
    </div>
  )
}

export default AppliedOffer
